import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { RecordDescription, RecordMetadata } from "@/components/record-link";
import {
  locationDetailingProductsPath,
  locationDetailingProductPath,
  editLocationDetailingProductPath,
  newLocationDetailingProductStockAdjustmentPath,
} from "@/routes";
import { quantity, type Product, type Adjustment } from "./types";

export default function ProductShow({
  product,
  adjustments,
  page,
  more,
}: {
  product: Product;
  adjustments: Adjustment[];
  page: number;
  more: boolean;
}) {
  const { auth, i18n: context } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const amount = (value: string | null) =>
    value === null
      ? t("inventory.disabled")
      : `${quantity(value, i18n.language)} ${t(`inventory.${product.unit}`)}`;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(locationId),
        },
      ]}
    >
      <Head title={product.name} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display break-words">{product.name}</h1>
          {auth.user?.role === "admin" && (
            <Button
              variant="outline"
              render={
                <Link
                  href={editLocationDetailingProductPath(
                    locationId,
                    product.id,
                  )}
                />
              }
            >
              {t("inventory.edit")}
            </Button>
          )}
        </div>
        <Card>
          <CardContent className="space-y-4 pt-6">
            <RecordDescription>
              <span className="text-muted-foreground">
                {t("inventory.balance")}:{" "}
              </span>
              <span className="text-lg font-semibold tabular-nums">
                {amount(product.quantity)}
              </span>
            </RecordDescription>
            <RecordMetadata>
              {t("inventory.threshold")}: {amount(product.threshold)}
            </RecordMetadata>
            {product.low_stock || Number(product.quantity) === 0 ? (
              <Badge
                variant="destructive"
                className="h-auto whitespace-normal py-1 text-sm font-semibold"
              >
                {[
                  product.low_stock ? t("inventory.low") : null,
                  Number(product.quantity) === 0
                    ? t("workflow.stock_empty")
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Badge>
            ) : (
              product.threshold !== null && (
                <RecordMetadata>{t("inventory.normal")}</RecordMetadata>
              )
            )}
            <div className="flex flex-wrap gap-3">
              {(["receipt", "correction", "threshold"] as const).map((kind) => (
                <Button
                  key={kind}
                  variant={kind === "receipt" ? "default" : "outline"}
                  render={
                    <Link
                      href={newLocationDetailingProductStockAdjustmentPath(
                        locationId,
                        product.id,
                        { kind },
                      )}
                    />
                  }
                >
                  {t(
                    kind === "threshold"
                      ? "inventory.threshold_action"
                      : `inventory.${kind}`,
                  )}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
        <h2 className="text-xl font-semibold">{t("inventory.history")}</h2>
        <Card>
          <CardContent className="divide-y">
            {adjustments.length === 0 && (
              <p className="text-muted-foreground py-6">
                {t("inventory.no_history")}
              </p>
            )}
            {adjustments.map((adjustment) => (
              <article key={adjustment.id} className="space-y-2 py-5">
                <h3 className="font-semibold">
                  {t(`inventory.kind_${adjustment.kind}`)}
                </h3>
                <RecordDescription>
                  {t(
                    adjustment.kind === "threshold"
                      ? "inventory.threshold_change"
                      : "inventory.quantity_change",
                    {
                      before: amount(
                        adjustment.kind === "threshold"
                          ? adjustment.threshold_before
                          : adjustment.quantity_before,
                      ),
                      after: amount(
                        adjustment.kind === "threshold"
                          ? adjustment.threshold_after
                          : adjustment.quantity_after,
                      ),
                      change: amount(adjustment.quantity_change),
                    },
                  )}
                </RecordDescription>
                <RecordDescription className="whitespace-pre-wrap">
                  {adjustment.reason}
                </RecordDescription>
                <RecordMetadata>
                  {t("inventory.by", {
                    actor: adjustment.actor,
                    time: new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: context.time_zone,
                    }).format(new Date(adjustment.created_at)),
                  })}
                </RecordMetadata>
              </article>
            ))}
          </CardContent>
        </Card>
        <nav
          aria-label={t("inventory.history")}
          className="flex flex-wrap gap-3"
        >
          {page > 1 && (
            <Button
              variant="outline"
              render={
                <Link
                  href={locationDetailingProductPath(locationId, product.id, {
                    page: page - 1,
                  })}
                />
              }
            >
              {t("inventory.previous")}
            </Button>
          )}
          {more && (
            <Button
              variant="outline"
              render={
                <Link
                  href={locationDetailingProductPath(locationId, product.id, {
                    page: page + 1,
                  })}
                />
              }
            >
              {t("inventory.next")}
            </Button>
          )}
        </nav>
      </PageCanvas>
    </AppLayout>
  );
}
